import { useEffect, useState } from 'react';
import googlePlacesApi from '../components/trashie/scripts/googlePlacesApi';

// Loads the Google Maps JS API (Places library) on demand — only when a component
// that needs address autocomplete mounts — instead of on every page in App.js.
//
// Module-level singleton so the script is injected at most once no matter how many
// address forms mount, and consumers get an `isLoaded` signal to (re)initialize their
// autocomplete once Places is ready. This is required because the script is async: the
// old app-wide load happened seconds before the address step, so it was usually ready;
// loading on demand compresses that window, so consumers must react to readiness rather
// than reading `window.google.maps` synchronously at mount.
let loadPromise = null;
let ready = false;
const subscribers = new Set();

const placesReady = () => Boolean(
  typeof window !== 'undefined'
  && window.google
  && window.google.maps
  && window.google.maps.places
  && window.google.maps.places.Autocomplete,
);

const markReady = () => {
  ready = true;
  subscribers.forEach((fn) => fn());
};

const loadGoogleMapsPlaces = () => {
  if (ready || placesReady()) {
    ready = true;
    return Promise.resolve();
  }
  if (loadPromise) {
    return loadPromise;
  }
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }

  loadPromise = new Promise((resolve) => {
    const done = async () => {
      try {
        // With loading=async the Places namespace is populated via importLibrary;
        // await it so `window.google.maps.places.Autocomplete` exists before we
        // report ready. (libraries=places also loads it as a fallback.)
        if (window.google && window.google.maps && window.google.maps.importLibrary) {
          await window.google.maps.importLibrary('places');
        }
      } catch (e) {
        // If importLibrary fails, placesReady() below still gates consumers.
      }
      if (placesReady()) {
        markReady();
      }
      resolve();
    };

    // Script failed to load (offline, CSP, ad-blocker blocking maps.googleapis.com).
    // Degrade gracefully: log for observability (manual address entry still works),
    // then drop the dead tag + cached promise so a later mount can retry instead of
    // being stuck on a promise that never resolves.
    const fail = (e) => {
      // eslint-disable-next-line no-console
      console.warn('Google Maps Places failed to load; address autocomplete disabled (manual entry still works).', e);
      const tag = document.querySelector('script[data-google-maps-places]');
      if (tag) tag.remove();
      loadPromise = null;
      resolve();
    };

    // Reuse an already-injected tag (e.g. React StrictMode double-invoke, or a
    // prior mount) instead of stacking duplicate scripts.
    const existing = document.querySelector('script[data-google-maps-places]');
    if (existing) {
      if (placesReady()) {
        done();
      } else {
        existing.addEventListener('load', done, { once: true });
        existing.addEventListener('error', fail, { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = googlePlacesApi;
    script.async = true;
    script.setAttribute('data-google-maps-places', 'true');
    script.addEventListener('load', done, { once: true });
    script.addEventListener('error', fail, { once: true });
    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * Loads extra Maps JS libraries ('maps', 'geocoding', …) on top of the same single
 * script tag the Places loader injects, so a page that needs both still loads the
 * API once. Resolves `true` only when every requested library imported; `false` on
 * any failure (script blocked, key not enabled for that API, offline) so callers can
 * degrade — the GoLocker map simply hides and its list still renders.
 */
const IMPORT_LIBRARY_WAIT_MS = 5000;
const IMPORT_LIBRARY_POLL_MS = 100;

// The script's `load` event can fire before `google.maps.importLibrary` is defined
// (observed with loading=async: the namespace exists, the bootstrap is not done),
// and the Places loader resolves regardless of whether Places came up. So poll
// briefly for the bootstrap instead of reading it once.
const waitForImportLibrary = async () => {
  const deadline = Date.now() + IMPORT_LIBRARY_WAIT_MS;
  while (Date.now() < deadline) {
    const maps = typeof window !== 'undefined' ? window.google?.maps : undefined;
    if (maps?.importLibrary) return maps;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => { setTimeout(resolve, IMPORT_LIBRARY_POLL_MS); });
  }
  return null;
};

export const loadGoogleMapsLibraries = async (names = []) => {
  await loadGoogleMapsPlaces();
  const maps = await waitForImportLibrary();
  if (!maps) return false;
  try {
    await Promise.all(names.map((name) => maps.importLibrary(name)));
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`Google Maps libraries [${names.join(', ')}] failed to load.`, e);
    return false;
  }
};

/**
 * Ensures the Google Maps Places library is loaded and returns `{ isLoaded }`.
 * Consumers should include `isLoaded` in the dependency array of the effect that
 * initializes autocomplete so it (re)runs once Places becomes available.
 */
const useGoogleMapsPlaces = () => {
  const [isLoaded, setIsLoaded] = useState(() => ready || placesReady());

  useEffect(() => {
    if (isLoaded) {
      return undefined;
    }
    let active = true;
    const onReady = () => {
      if (active) setIsLoaded(true);
    };
    subscribers.add(onReady);
    loadGoogleMapsPlaces().then(() => {
      if (active && ready) setIsLoaded(true);
    });
    return () => {
      active = false;
      subscribers.delete(onReady);
    };
  }, [isLoaded]);

  return { isLoaded };
};

export default useGoogleMapsPlaces;
